import React, { useState, useEffect, useCallback } from "react";
import { Table, NumberInput, Card, Grid, Tooltip, Text, Group, Box, ThemeIcon, Badge } from "@mantine/core";
import { IconCurrencyDollar, IconPercentage, IconBuildingStore } from "@tabler/icons-react";
import { checkUser } from "../../services/authServices";
import "../styles/main.css";

const Roi = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [leads, setLeads] = useState(100);
  const [approveRate, setApproveRate] = useState(40);
  const [trashRate, setTrashRate] = useState(0);
  const [payout, setPayout] = useState(20);
  const [data, setData] = useState([]);

  useEffect(() => {
    checkUser().then((res) => {
      setIsLoggedIn(res.isLoggedIn);
    });
  }, [isLoggedIn]);

  const calculateROI = useCallback((costPerLead) => {
    const totalCost = leads * costPerLead;
    const validLeads = leads * (1 - trashRate / 100);
    const approvedLeads = validLeads * (approveRate / 100);
    const revenue = approvedLeads * payout;
    const profit = revenue - totalCost;
    const roi = totalCost > 0 ? ((revenue - totalCost) / totalCost) * 100 : 0;

    return {
      costPerLead,
      totalCost: totalCost.toFixed(2),
      approvedLeads: Math.round(approvedLeads),
      revenue: revenue.toFixed(2),
      profit: profit.toFixed(2),
      roi: roi.toFixed(2),
    };
  }, [leads, trashRate, approveRate, payout]);

  const generatePriceBreakdown = (price) => {
    const breakdown = [];
    for (let i = 0.1; i < 1; i += 0.1) {
      const exactPrice = price + i;
      const calculation = calculateROI(exactPrice);
      breakdown.push(
        <Group key={i} justify="space-between" gap="xs" mb={4}>
          <Text size="xs" fw={500}>${exactPrice.toFixed(2)}</Text>
          <Badge
            size="xs"
            variant="light"
            color={calculation.roi >= 0 ? "teal" : "red"}
          >
            ROI: {calculation.roi}%
          </Badge>
        </Group>
      );
    }
    return <div style={{ minWidth: 150, padding: 4 }}>{breakdown}</div>;
  };

  useEffect(() => {
    const newData = [];
    for (let i = 1; i <= 20; i++) {
      newData.push(calculateROI(i));
    }
    setData(newData);
  }, [calculateROI]);

  const firstHalf = data.slice(0, 10);
  const secondHalf = data.slice(10);

  const renderTable = (dataSource) => (
    <Table.ScrollContainer minWidth={300}>
      <Table highlightOnHover verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>CPL ($)</Table.Th>
            <Table.Th>Total Cost</Table.Th>
            <Table.Th ta="center">Approved</Table.Th>
            <Table.Th>Revenue</Table.Th>
            <Table.Th>Profit</Table.Th>
            <Table.Th ta="right">ROI (%)</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {dataSource.map((row) => (
            <Table.Tr key={row.costPerLead}>
              <Table.Td>
                <Tooltip label={generatePriceBreakdown(row.costPerLead)} position="right" withArrow shadow="md">
                  <Text size="sm" fw={600} style={{ cursor: "pointer", color: "#665bd1", textDecoration: "underline", textDecorationStyle: "dotted" }}>
                    ${row.costPerLead.toFixed(2)}
                  </Text>
                </Tooltip>
              </Table.Td>
              <Table.Td fw={500}>${parseFloat(row.totalCost).toLocaleString()}</Table.Td>
              <Table.Td ta="center" fw={500}>{row.approvedLeads}</Table.Td>
              <Table.Td fw={500}>${parseFloat(row.revenue).toLocaleString()}</Table.Td>
              <Table.Td>
                <Text fw={700} c={row.profit >= 0 ? "teal" : "red"} size="sm">
                  ${parseFloat(row.profit).toLocaleString()}
                </Text>
              </Table.Td>
              <Table.Td ta="right">
                <Badge variant="light" color={row.roi >= 0 ? "teal" : "red"} size="sm" radius="sm">
                  {row.roi}%
                </Badge>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );

  return (
    <div>
      <div className="workspace-heading"><div><h2>ROI Calculator</h2><p>Model your costs and compare performance across CPL ranges.</p></div></div>
      {/* 4 Beautiful KPI Stats Cards */}
      <Grid mb="xl" gutter="md">
        <Grid.Col span={{ base: 6, sm: 6, md: 3 }}>
          <div className="kpi-card">
            <Group justify="space-between" wrap="nowrap">
              <div>
                <Text className="kpi-title">Total Input Leads</Text>
                <Text className="kpi-value">{leads.toLocaleString()}</Text>
              </div>
              <ThemeIcon size="lg" variant="light" color="brand" radius="md">
                <IconBuildingStore size={20} />
              </ThemeIcon>
            </Group>
          </div>
        </Grid.Col>

        <Grid.Col span={{ base: 6, sm: 6, md: 3 }}>
          <div className="kpi-card green">
            <Group justify="space-between" wrap="nowrap">
              <div>
                <Text className="kpi-title">Approved Leads</Text>
                <Text className="kpi-value">
                  {Math.round(leads * (1 - trashRate / 100) * (approveRate / 100))}
                </Text>
              </div>
              <ThemeIcon size="lg" variant="light" color="teal" radius="md">
                <IconPercentage size={20} />
              </ThemeIcon>
            </Group>
          </div>
        </Grid.Col>

        <Grid.Col span={{ base: 6, sm: 6, md: 3 }}>
          <div className="kpi-card orange">
            <Group justify="space-between" wrap="nowrap">
              <div>
                <Text className="kpi-title">Approve Rate</Text>
                <Text className="kpi-value">{approveRate}%</Text>
              </div>
              <ThemeIcon size="lg" variant="light" color="orange" radius="md">
                <IconPercentage size={20} />
              </ThemeIcon>
            </Group>
          </div>
        </Grid.Col>

        <Grid.Col span={{ base: 6, sm: 6, md: 3 }}>
          <div className="kpi-card green">
            <Group justify="space-between" wrap="nowrap">
              <div>
                <Text className="kpi-title">Estimated Revenue</Text>
                <Text className="kpi-value">
                  ${(leads * (1 - trashRate / 100) * (approveRate / 100) * payout).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Text>
              </div>
              <ThemeIcon size="lg" variant="light" color="teal" radius="md">
                <IconCurrencyDollar size={20} />
              </ThemeIcon>
            </Group>
          </div>
        </Grid.Col>
      </Grid>

      {/* Inputs Section */}
      <Card withBorder shadow="sm" radius="md" mb="xl" style={{ border: "1px solid #e7e7eb", backgroundColor: "#ffffff" }}>
        <Box p="md" style={{ borderBottom: "1px solid #f5f5f7" }}>
          <Text fw={600} size="sm" c="dark.5">
            Simulation Parameters
          </Text>
        </Box>
        <Box p="md">
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <NumberInput
                label="Number of Leads"
                min={1}
                value={leads}
                onChange={(val) => setLeads(val || 1)}
                leftSection={<IconBuildingStore size={16} />}
                size="sm"
                radius="md"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <NumberInput
                label="Approve Rate (%)"
                min={0}
                max={100}
                value={approveRate}
                onChange={(val) => setApproveRate(val || 0)}
                leftSection={<IconPercentage size={16} />}
                size="sm"
                radius="md"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <NumberInput
                label="Trash Rate (%)"
                min={0}
                max={100}
                value={trashRate}
                onChange={(val) => setTrashRate(val || 0)}
                leftSection={<IconPercentage size={16} />}
                size="sm"
                radius="md"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <NumberInput
                label="Payout per Approve ($)"
                min={1}
                value={payout}
                onChange={(val) => setPayout(val || 1)}
                leftSection={<IconCurrencyDollar size={16} />}
                size="sm"
                radius="md"
              />
            </Grid.Col>
          </Grid>
        </Box>
      </Card>

      {/* Cost Ranges */}
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder shadow="sm" radius="md" style={{ border: "1px solid #e7e7eb", backgroundColor: "#ffffff" }}>
            <Box p="md" style={{ borderBottom: "1px solid #f5f5f7" }}>
              <Text fw={600} size="sm" c="dark.5">
                CPL Ranges ($1.00 - $10.00)
              </Text>
            </Box>
            <Box p="md">
              {renderTable(firstHalf)}
            </Box>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder shadow="sm" radius="md" style={{ border: "1px solid #e7e7eb", backgroundColor: "#ffffff" }}>
            <Box p="md" style={{ borderBottom: "1px solid #f5f5f7" }}>
              <Text fw={600} size="sm" c="dark.5">
                CPL Ranges ($11.00 - $20.00)
              </Text>
            </Box>
            <Box p="md">
              {renderTable(secondHalf)}
            </Box>
          </Card>
        </Grid.Col>
      </Grid>
    </div>
  );
};

export default Roi;
